import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Mui from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';

import { SHOW_SHORTCUTS, TIPO_DOCUMENTO, STATUS_MONITOR_NFCE, DOCUMENTO_TRANSMISSAO } from '~/constants';
import { getApplicationIsOnline } from '~/store/modules/app/app-selectors';
import * as monitorActions from '~/store/modules/monitor/monitor-actions';

import DocumentosaidaTable from '../../components/DocumentosaidaTable';
import ModalVisualizarDocumento from '~/pages/PainelVenda/modal/ModalVisualizarDocumento';
import ModalImportarDocumento from '~/pages/TelaVenda/modal/ModalImportarDocumento';
import ModalPreviewDanfe from '~/pages/TelaVenda/modal/ModalPreviewDanfe';
import { DocumentoSaidaOnlineService } from '~/store/modules/documento-saida/documento-saida.service';
import { selectorRotina } from '~/store/modules/auth/auth-selectors';
import { ordenarPorRelevancia, TIPO_PESQUISA } from '~/utils/weighted-search';
import { getFilterByData } from '~/store/modules/monitor/monitor-helper';

const useStyles = Mui.makeStyles(theme => ({
  badgeZero: {
    '&.MuiBadge-badge': {
      backgroundColor: theme.palette.background.darkest,
      color: theme.palette.text.primary,
    },
  },
  badgeMany: {
    '&.MuiBadge-badge': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
}));

const statusOptions = [
  {
    value: STATUS_MONITOR_NFCE.DOCUMENTOS,
    label: 'Documentos'
  },
  {
    value: STATUS_MONITOR_NFCE.NAO_TRANSMITIDO,
    label: 'Aguard. transmissao'
  },
  {
    value: STATUS_MONITOR_NFCE.TRANSMITIDOS,
    label: 'Transmitidos'
  },
  {
    value: STATUS_MONITOR_NFCE.CANCELADOS,
    label: 'Cancelados'
  }
];

const Documentosaida = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState('');

  const isOnline = useSelector(getApplicationIsOnline());
  const { terminal } = useSelector(state => state.app);
  const { empresa } = useSelector(state => state.auth);
  const {
    loading,
    filtroData,
    filtroDataOptions,
    status,
    totalizadores,
    page,
    rowsPerPage
  } = useSelector(state => state.monitor);
  const rotinaF180DSA = useSelector(selectorRotina('F180DSA')) // F180DSA - Não permite ao usuário copiar documento de saída.
  const [showModalImportarDocumento, setShowModalImportarDocumento] = useState({
    show: false
  });
  const [showModalDanfe, setShowModalDanfe] = useState({
    show: false
  });
  const [showModalVisualizarDocumento, setShowModalVisualizarDocumento] = useState({
    show: false
  });
  const anchorRef = useRef();

    const handleSearch = async (goBackToFirstPage) => {
      try {
        dispatch(monitorActions.setLoading(true));

        let conditions = []
        let orderby = ''

        let tipoPesquisa = null
        if (/^\d+$/.test(searchTerm)) { // números
          tipoPesquisa = TIPO_PESQUISA.NUMEROS_INTEIROS
          orderby = `documentoNumero asc, xPed asc, xCont asc, valorTotal asc`
          conditions.push(`contains(cast(documentoNumero, Edm.String),'${searchTerm}')`)
          conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm}')`)
          conditions.push(`contains(xPed,'${searchTerm}')`)
          conditions.push(`contains(xCont,'${searchTerm}')`)
        }
        else if (/^(?=.*\d)(?=.*[.,])[\d.,]+$/.test(searchTerm)) { // numeros + ponto ou virgula
          tipoPesquisa = TIPO_PESQUISA.NUMEROS_FLUTUANTES
          orderby = `valorTotal asc`
          conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm.replace(/,/g, ".")}')`)
        }
        else if (/^[\p{L}\s]+$/u.test(searchTerm)) { // texto
          tipoPesquisa = TIPO_PESQUISA.TEXTO
          orderby = `clienteNome asc, fatVendedor/nome asc`
          conditions.push(`contains(clienteNome,'${searchTerm}')`)
          conditions.push(`contains(fatVendedor/nome,'${searchTerm}')`)
        }
        else { // misto
          tipoPesquisa = TIPO_PESQUISA.MISTO
          orderby = `documentoNumero asc, clienteNome asc, fatVendedor/nome asc, xPed asc, xCont asc`
          conditions.push(`contains(cast(documentoNumero, Edm.String),'${searchTerm}')`)
          conditions.push(`contains(cast(valorTotal, Edm.String),'${searchTerm}')`)
          conditions.push(`contains(xPed,'${searchTerm}')`)
          conditions.push(`contains(xCont,'${searchTerm}')`)
          conditions.push(`contains(clienteNome,'${searchTerm}')`)
          conditions.push(`contains(fatVendedor/nome,'${searchTerm}')`)
        }

        let filter = [conditions.length ? `(${conditions.join(' or ')})` : '']
        filter.push(...getFilterByData(filtroData, 'documentoDataEmissao'))

        switch (status) {
          case STATUS_MONITOR_NFCE.DOCUMENTOS:
            filter.push(`(documentoModelo eq '01')`)
            filter.push(`(documentoCancelado eq false)`)
            break;
          case STATUS_MONITOR_NFCE.NAO_TRANSMITIDO:
            filter.push(`(documentoModelo eq '65')`)
            filter.push(`(documentoCancelado eq false)`)
            filter.push(`(documentoTransmissao eq ${DOCUMENTO_TRANSMISSAO.NAO_TRANSMITIDO} or documentoTransmissao eq ${DOCUMENTO_TRANSMISSAO.AGUARDANDO_TRANSMISSAO} or documentoTransmissao eq ${DOCUMENTO_TRANSMISSAO.EM_PROCESSAMENTO})`)
            break;
          case STATUS_MONITOR_NFCE.TRANSMITIDOS:
            filter.push(`(documentoModelo eq '65')`)
            filter.push(`(documentoCancelado eq false)`)
            filter.push(`(documentoTransmissao eq ${DOCUMENTO_TRANSMISSAO.TRANSMITIDO})`)
            break;
          case STATUS_MONITOR_NFCE.CANCELADOS:
            filter.push(`(documentoModelo eq '01' or documentoModelo eq '65')`)
            filter.push(`(documentoCancelado eq true)`)
            break;
        }

        let newPage = goBackToFirstPage ? 0 : page

        const service = new DocumentoSaidaOnlineService();
        const res = await service.getAllDocumentos({
          offset: newPage === 0 ? 0 : newPage * rowsPerPage,
          limit: rowsPerPage,
          filter: filter,
          orderby: orderby,
        });

        if (goBackToFirstPage) {
          dispatch(monitorActions.setPage(0));
        }
        
        const items = ordenarPorRelevancia(res.data.items, searchTerm, TIPO_DOCUMENTO.DOCUMENTOSAIDA, tipoPesquisa)

        let totalizadores = {}
        const { data } = await service.getTotalizadores(filtroData, searchTerm);
        totalizadores = { ...data };

        dispatch(monitorActions.loadDocumentosSearchSuccess({
          documentos: items,
          documentosAll: items,
          valorTotalDocumentos: items.reduce((total, item) => total + item.valorTotal, 0),
          quantidadeDocumentos: res.data.count ?? items.length,
          totalizadores: totalizadores,
        }));

      } catch (err) {
        throw err;
      } finally {
        dispatch(monitorActions.setLoading(false));
      }
    };

  const handleTabsChange = (event, value) => {
    dispatch(monitorActions.setStatus(value));
  };

  useEffect(() => {
    if (showModalImportarDocumento.show) {
      return
    }
    if(searchTerm) {
      handleSearch(false)
    } 
    else {
      dispatch(monitorActions.loadDocumentosRequest());
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (showModalImportarDocumento.show) {
      return
    }
    if(searchTerm) {
      handleSearch(true)
    } 
    else {
      dispatch(monitorActions.setPage(0));
      dispatch(monitorActions.loadDocumentosRequest());
    }
  }, [status, filtroData]);

  useEffect(() => {
    dispatch(monitorActions.setStatus(STATUS_MONITOR_NFCE.DOCUMENTOS));
    return () => {
      dispatch(monitorActions.limparDados());
    };
  }, [isOnline]);

  // useEffect(() => {
  //   if(searchTerm) {
  //     const delayDebounceFn = setTimeout(() => {
  //       handleSearch()
  //     }, 1000)
  
  //     return () => clearTimeout(delayDebounceFn)
  //   }
  // }, [searchTerm])

  return (
    <>
      <Mui.Grid container>
        <Mui.Grid item md={12}>
          <Mui.Box p={2} display="flex" justifyContent="space-between">
            <Mui.Box style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginRight: '16px', flex: '0 1 auto' }}>
              <Mui.Button
                variant="contained"
                color="primary"
                disabled={terminal === null}
                style={{ height: 44, whiteSpace: 'normal' }}
                className='button-success'
                onClick={() => {
                  history.push('/pdv', {
                    tipoDocumento: TIPO_DOCUMENTO.DOCUMENTOSAIDA
                  });
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MuiIcons.AddCircle style={{position: 'relative', bottom: '2px'}}/>
                  <span style={{ verticalAlign: 'middle' }}>Documento de saída {SHOW_SHORTCUTS && '(CTRL+D)'}</span>
                </span>
              </Mui.Button>
              <Mui.Button
                variant="contained"
                disabled={terminal === null}
                style={{ height: 44, whiteSpace: 'normal' }}
                className='button-white'
                onClick={() =>
                  setShowModalImportarDocumento({ show: true, type: TIPO_DOCUMENTO.PEDIDO })
                }
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MuiIcons.ImportExport style={{position: 'relative', bottom: '2px'}}/>
                  <span style={{ verticalAlign: 'middle' }}>Importar</span>
                </span>
              </Mui.Button>
              <Mui.Tooltip title={(
                rotinaF180DSA === true
              ) ? <>
                {rotinaF180DSA === true ?
                'Usuário sem permissão para copiar documento de saída!' : ''}
              </> : ''}>
                <Mui.Box>
                  <Mui.Button
                    variant="contained"
                    disabled={terminal === null || rotinaF180DSA === true}
                    style={{ height: 44, whiteSpace: 'normal' }}
                    className='button-white'
                    onClick={() =>
                      setShowModalImportarDocumento({ show: true, type: TIPO_DOCUMENTO.DOCUMENTOSAIDA })
                    }
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MuiIcons.FileCopy style={{position: 'relative', bottom: '2px'}}/>
                      <span style={{ verticalAlign: 'middle' }}>Copiar</span>
                    </span>
                  </Mui.Button>
                </Mui.Box>
              </Mui.Tooltip>
            </Mui.Box>

            <Mui.Box display="flex" alignItems={'flex-start'} style={{ flex: '1 1 auto' }}>
              <Mui.Tooltip title="Buscar por nº do documento, cliente, vendedor, pedido de compra, contrato ou valor...">
                <Mui.Box display="inline-block" style={{flex: '1 0 auto'}}>
                  <Mui.TextField
                    disabled={loading}
                    style={{width: '100%'}}
                    InputProps={{
                      style: {
                        height: 44
                      },
                      startAdornment: (
                        <Mui.InputAdornment position="start">
                          <Mui.SvgIcon fontSize="small" color="action">
                            <MuiIcons.Search />
                          </Mui.SvgIcon>
                        </Mui.InputAdornment>
                      )
                    }}
                    placeholder="Pesquisar documento..."
                    value={searchTerm}
                    variant="outlined"
                    onChange={(event) => setSearchTerm(event.target.value)}
                    onKeyPress={(event) => {
                      if (event.key === 'Enter') {
                        if (searchTerm.trim() === '') {
                          setSearchTerm('')
                          dispatch(monitorActions.setPage(0));
                          dispatch(monitorActions.loadDocumentosRequest())
                        } else {
                          handleSearch(true);
                        }
                      }
                    }}
                  />
                </Mui.Box>
              </Mui.Tooltip>
              <Mui.ButtonGroup
                variant="contained"
                color="default"
                ref={anchorRef}
                aria-label="split button"
                style={{ marginLeft: 16, minHeight: 44, flex: '0 1 auto' }}
              >
                {filtroDataOptions.map((f, index) => (
                  <Mui.Button
                    key={`pedido-filtro-data-${index}`}
                    color={f.filtro === filtroData ? 'primary' : 'default'}
                    style={{ whiteSpace: 'normal' }}
                    className={f.filtro === filtroData ? 'button-primary' : 'button-white'}
                    onClick={() => {
                      dispatch(monitorActions.setPage(0));
                      dispatch(monitorActions.setRowsPerPage(10));
                      dispatch(monitorActions.setFiltroData(f.filtro))
                    }}
                  >
                    {f.titulo}
                  </Mui.Button>
                ))}
              </Mui.ButtonGroup>
            </Mui.Box>
          </Mui.Box>

          <Mui.Tabs
            onChange={handleTabsChange}
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            value={status}
            variant="scrollable"
          >
            {statusOptions
              ?.map(s => ({ ...s, count: totalizadores ? totalizadores[s?.value] ?? 0 : 0 }))
              ?.map(tab => (
                <Mui.Tab
                  key={tab?.value}
                  value={tab?.value}
                  label={
                    <Mui.Box>
                      <Mui.Badge
                        badgeContent={tab?.count ?? 0}
                        color="default"
                        classes={{
                          badge:
                            (tab?.count ?? 0) === 0
                              ? classes.badgeZero
                              : classes.badgeMany,
                        }}
                        max={9999}
                        showZero
                      />
                      <Mui.Typography>{tab?.label}</Mui.Typography>
                    </Mui.Box>
                  }
                />
              ))}
          </Mui.Tabs>

          <Mui.Divider />

          <DocumentosaidaTable />
        </Mui.Grid>
      </Mui.Grid>
      {showModalImportarDocumento.show && (
        <ModalImportarDocumento
          type={showModalImportarDocumento.type}
          show={showModalImportarDocumento.show}
          onClose={() => {
            dispatch(monitorActions.setPage(0));
            dispatch(monitorActions.setRowsPerPage(10));
            setShowModalImportarDocumento({ show: false })
          }}
          callback={data => {
            history.push('/pdv', data);
          }}
        />
      )}
      {showModalDanfe.show && (
        <ModalPreviewDanfe
          data={{ documentoId: showModalDanfe?.data?.id }}
          showModal={showModalDanfe.show}
          onClose={() => setShowModalDanfe({ show: false })}
        />
      )}
      {showModalVisualizarDocumento.show && (
        <ModalVisualizarDocumento
          data={{ documentoId: showModalVisualizarDocumento?.data?.id }}
          showModal={showModalVisualizarDocumento.show}
          onClose={() => setShowModalVisualizarDocumento({ show: false })}
        />
      )}
    </>
  );
};

export default Documentosaida;
